import { useFloorPlan } from '../../hooks/useFloorPlan'
import { useIsEditMode } from '@/hooks/useIsEditMode'

import { Box, Checkbox, Flex, Icon, Skeleton, Text } from '@chakra-ui/react'

import { SecurityDevice } from '@/components/icons'
import {
  DragEventI,
  Draggable,
  DrawerContainer,
  DrawerScrollContainer,
  SearchInput,
} from '@/components/ui'

import { DeviceI } from '../../types/types'
import { DeviceListItem } from './DeviceListItem'

interface IProps {
  devices: DeviceI[]
  onTileDragStart: (event: DragEventI) => void
  onDeviceClicked: (device) => void
  onDeviceItemHoverIn: (deviceId: string) => void
  onDeviceItemHoverOut: () => void
}

export const DevicesPanel = ({
  devices,
  onTileDragStart,
  onDeviceClicked,
  onDeviceItemHoverIn,
  onDeviceItemHoverOut,
}: IProps) => {
  // Keep as old Edit Mode check!
  const isEditMode = useIsEditMode()
  const {
    nameLikeFilter,
    setNameLikeFilter,
    isFloorDataLoading,
    shouldShowAssociatedDevices,
    setShouldShowAssociatedDevices,
  } = useFloorPlan()

  return (
    <DrawerContainer w='390px'>
      <Flex
        boxShadow='xl'
        direction='column'
        padding='16px'
        paddingBottom='20px'
      >
        <Flex align='center' direction='row' paddingBottom='20px' w='100%'>
          <Icon as={SecurityDevice} boxSize='24px' strokeColor='#000' />
          <Text
            data-testid='floorPlanPage_devicePanel_devices'
            fontSize='24px'
            fontWeight='bold'
            marginLeft='9px'
            userSelect='none'
          >
            Device Placement
          </Text>
        </Flex>
        <SearchInput
          onChange={(e) => setNameLikeFilter(e.target.value)}
          placeholder='Search for Device'
          value={nameLikeFilter}
        />

        <Flex align='flex-start' mt='10px'>
          <Checkbox
            isChecked={shouldShowAssociatedDevices}
            onChange={(e) => setShouldShowAssociatedDevices(e.target.checked)}
            size='md'
          >
            <Text fontSize='sm' fontWeight='semi-bold'>
              Show Associated Devices
            </Text>
          </Checkbox>
        </Flex>
      </Flex>
      <DrawerScrollContainer>
        {isFloorDataLoading ? (
          <>
            {Array.from({ length: 8 }).map((_, i) => (
              <Flex
                align='center'
                key={`device-skeleton-${i}`}
                padding='10px'
                width='100%'
              >
                <Skeleton borderRadius='4px' h='35px' w='35px' />
                <Skeleton h='16px' ml='10px' w='60%' />
              </Flex>
            ))}
          </>
        ) : devices.length === 0 ? (
          <Flex align='center' flex={1} h='100%' justify='center'>
            <Text color='gray.600' fontWeight={500}>
              No devices found.
            </Text>
          </Flex>
        ) : (
          devices.map((d) => (
            <Draggable
              dragData={d}
              dragEnabled={isEditMode}
              key={d.id}
              onDragStart={onTileDragStart}
            >
              <Box onClick={() => onDeviceClicked(d)}>
                <DeviceListItem
                  deviceHealth={d.deviceHealth}
                  deviceId={d.id}
                  deviceName={d.deviceName || d.id}
                  deviceType={d.deviceType}
                  onDeviceItemHoverIn={onDeviceItemHoverIn}
                  onDeviceItemHoverOut={onDeviceItemHoverOut}
                  placed={d.placed}
                  selected={d.isEditMode}
                />
              </Box>
            </Draggable>
          ))
        )}
      </DrawerScrollContainer>
    </DrawerContainer>
  )
}
